import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  viewChild,
  inject,
  ViewChild,
} from '@angular/core';
import { UsersService } from 'src/app/services/users/users.service';
import { UserInfo } from '../../models/user-info.model';
import { OrgsService } from 'src/app/services/orgs/orgs.service';
import { entAnimations } from 'src/app/animations';
import {
  catchError,
  map,
  Observable,
  of,
  startWith,
  Subscription,
  switchMap,
} from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { SettingsService } from 'src/app/services/settings/settings.service';
import { OrgModel } from 'src/app/models/org.model';

@Component({
  selector: 'app-orgs',
  templateUrl: './orgs.component.html',
  styleUrls: ['./orgs.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: entAnimations,
  standalone: false,
})
export class OrgsComponent implements OnInit, AfterViewInit, OnDestroy {
  private _userService = inject(UsersService);
  private _settingsService = inject(SettingsService);
  private _orgsService = inject(OrgsService);

  readonly paginator = viewChild(MatPaginator);
  @ViewChild(MatSort, { static: false }) set content(sort: MatSort) {
    this.dataSource.sort = sort;
  }
  readonly filter = viewChild<ElementRef>('filter');

  private currentUser: UserInfo;
  private currentGeSub: Subscription;
  private currentGe = '';

  dataSource = new MatTableDataSource<OrgModel>();
  isLoading = false;
  displayedColumns: string[] = [];
  expandedElement: OrgModel | null;
  windowWidth: number = window.innerWidth;
  sortedValue: string;

  constructor() {
    this.currentUser = this._userService.currentUserValue; //No null check needed, it's done in one of the Guard (AuthGuard)
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator();
  }

  ngOnDestroy(): void {
    this.currentGeSub.unsubscribe();
  }

  ngOnInit(): void {
    this.updateDisplayedColumns();
    this.isLoading = true;

    // Retrives currentGE initial value
    this._settingsService
      .getUserSettingsFromStorage(this.currentUser.oid)
      .pipe(
        map(s => {
          let currentGe = s?.defaultGE;
          if (!currentGe) {
            currentGe = this.currentUser.groupEtablissement[0];
          }
          return currentGe;
        })
      )
      .subscribe(ge => (this.currentGe = ge));

    // Subscribe to currentGE changes and init data with the value fetched earlier
    this.currentGeSub = this._settingsService.currentGE$
      .pipe(
        startWith(this.currentGe),
        switchMap(ge => {
          this.isLoading = true;
          return this.getObservableOrgs(ge);
        })
      )
      .subscribe({
        next: o => {
          this.dataSource.data = o;
          this.isLoading = false;
        },
        error: () => (this.isLoading = false),
      });
  }

  private getObservableOrgs(
    groupeEtablissement: string
  ): Observable<OrgModel[]> {
    if (groupeEtablissement) {
      return this._orgsService
        .getMyOrgs(groupeEtablissement)
        .pipe(catchError(() => of([] as OrgModel[])));
    }
    return of([] as OrgModel[]);
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateDisplayedColumns();
    this.windowWidth = window.innerWidth;
  }

  private updateDisplayedColumns(): void {
    this.displayedColumns =
      window.innerWidth < 650 ? ['name', 'expand'] : ['name', 'email'];
  }

  clearFilter(): void {
    this.dataSource.filter = '';
    this.filter().nativeElement.value = '';
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  applyFilter(event: KeyboardEvent) {
    const filterValue = event.target as HTMLInputElement;
    this.dataSource.filter = filterValue.value.trim().toLocaleLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  toggleRow(element: OrgModel): void {
    this.expandedElement = this.expandedElement === element ? null : element;
  }
}
